import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem } from '@mui/material'
import { FC } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { stringifyError } from '../../../utils/utils-functions'
import { enqueueSnackbar } from 'notistack'
import { updateUser } from '../../../services/permissions/api'
import { useForm } from 'react-hook-form'
import { PermissionUser, UpdateUserData } from '../../../services/permissions/types'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import ControlledTextField from '../../controlledInputs/ControlledTextField'
import ControlledCheckbox from '../../controlledInputs/ControlledCheckbox'

export type EditUserProps = {
  onClose: (value: boolean) => void;
  open: boolean;
  user: Omit<PermissionUser, 'roles'>
}

const schema = yup.object().shape({
  id: yup.number().required('Required'),
  first_name: yup.string().required('Required'),
  last_name: yup.string().required('Required'),
  email: yup.string().email('Invalid email').required('Required'),
  password: yup.string(),
  status: yup.string().required('Required'),
  is_read_only: yup.boolean().required('Required')
})

const EditUserDialog: FC<EditUserProps> = ({ onClose, open, user }) => {
  const queryClient = useQueryClient()

  const { mutateAsync: editUser, isLoading: createUserLoading } = useMutation(updateUser)

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      password: '',
      status: user.status,
      is_read_only: user.is_read_only
    },
    mode: 'onBlur',
    resolver: yupResolver(schema)
  })

  const handlerClose = () => {
    onClose(false)
  }

  const handleEditUser = async (data: UpdateUserData) => {
    try {
      await editUser(data)
      queryClient.refetchQueries({
        queryKey: ['permissionUsers']
      })
      enqueueSnackbar('User updated', { variant: 'success' })
      onClose(false)
    } catch (e) {
      console.error(e)
      enqueueSnackbar(stringifyError(e), { variant: 'error' })
    }
  }

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={handlerClose}
    >
      <DialogTitle>
        Edit account of {user.first_name} {user.last_name}
      </DialogTitle>
      <DialogContent>
        <form
          id="edit-user-form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit(handleEditUser)}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <ControlledTextField
                name="first_name"
                control={control}
                error={errors.first_name}
                label="First Name"
                margin="normal"
                size="medium"/>
            </Grid>
            <Grid item xs={6}>
              <ControlledTextField
                name="last_name"
                control={control}
                error={errors.last_name}
                label="Last Name"
                margin="normal"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="email"
                control={control}
                error={errors.email}
                label="Email"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="password"
                control={control}
                error={errors.password}
                label="Password"
                size="medium"/>
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField
                name="status"
                control={control}
                error={errors.status}
                label="Status"
                size="medium"
                select>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </ControlledTextField>
            </Grid>
            <Grid item xs={12}>
              <ControlledCheckbox
                name="is_read_only"
                control={control}
                label="Read Only"
                labelPlacement="end"
                size="medium"/>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handlerClose}>Close</Button>
        <LoadingButton
          form="edit-user-form"
          loading={createUserLoading}
          type="submit"
          variant="contained">
          Update
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditUserDialog
